import React, {Dispatch, FC, useCallback, useEffect, useState} from 'react';
import {Alert, Button, Col, Modal, Row} from "react-bootstrap";
import {FaExpandAlt} from "react-icons/fa";
import ObjectService from "../../http/ObjectService";
import Loading from "../Loading";
import {useAuth} from "../../store/AuthContext";

import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';

interface PropsModal {
    visible: boolean;
    setVisible: Dispatch<boolean>;
    objectId: string | number | null;
    refreshTable: () => void;
}

function SimpleGallery(props: any) {
    useEffect(() => {
        let lightbox: any = new PhotoSwipeLightbox({
            gallery: '#' + props.galleryID,
            children: 'a',
            pswpModule: () => import('photoswipe'),
        });
        lightbox.init();

        return () => {
            lightbox.destroy();
            lightbox = null;
        };
    }, []);

    return (
        <div className="pswp-gallery" id={props.galleryID}>
            {props.images.map((image: any, index:any) => (
                <a
                    href={image.largeURL}
                    data-pswp-width={image.width}
                    data-pswp-height={image.height}
                    key={props.galleryID + '-' + index}
                    target="_blank"
                    rel="noreferrer"
                >
                    <img src={image.thumbnailURL} alt=""/>
                </a>
            ))}
        </div>
    );
}

const ObjectModalView: FC<PropsModal> = (props) => {
    const {user} = useAuth();
    const [object, setObject] = useState<any | null>(null);

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [needsRefresh, setNeedsRefresh] = useState<boolean>(false);
    const [fullscreen, setFullscreen] = useState<boolean>(false);

    const fetchObject = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await ObjectService.query({
                includes: ["categories", "users", "photos"],
                options: {where: { id: props.objectId } }
            });

            if (data && data.length > 0) {
                const objectData = data[0];
                if (objectData.action === "DELETE") {
                    setError('Объект был удалён');
                    setNeedsRefresh(true);
                }
                setObject(objectData);
            } else {
                setNeedsRefresh(true);
                setError("Объект не найден");
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [props.objectId]);


    useEffect(() => {
        if (props.visible) {
            void fetchObject();
        }
    }, [props.visible, fetchObject]);

    const handleHideModal = () => {
        if (needsRefresh) props.refreshTable();
        props.setVisible(false);
    };

    return (
        <Modal show={props.visible} size={"xl"} fullscreen={fullscreen ? true : "md-down"} scrollable={true}
               onHide={handleHideModal}>
            <div className="modal-header">
                <h5 className="mb-0">Просмотр объекта</h5>
                <div className={"d-flex gap-3"}>
                    <button type="button" className="btn-link" onClick={() => setFullscreen(!fullscreen)}>
                        <FaExpandAlt/>
                    </button>
                    <button type="button" className="btn-close" onClick={handleHideModal}></button>
                </div>
            </div>
            {loading
                ?
                <div style={{height: "200px"}}><Loading/></div>
                :
                <>
                    {object ?
                        <>
                            <Modal.Body>
                                {error && <Alert variant={"danger"} dismissible={true}>{error}</Alert>}

                                {object.action !== "DELETE" && (
                                 <>
                                     <Row className={"mb-3"}>
                                         <Col sm={"2"}>Категория</Col>
                                         <Col sm={"10"}>
                                             {object?.category?.name}
                                         </Col>
                                     </Row>
                                     <Row className={"mb-3"}>
                                         <Col sm={"2"}>Район</Col>
                                         <Col sm={"10"}>{object.district}</Col>
                                     </Row>
                                     <Row className={"mb-3"}>
                                         <Col sm={"2"}>Адрес</Col>
                                         <Col sm={"10"}>{object.address}</Col>
                                     </Row>
                                     <Row className={"mb-3"}>
                                         <Col sm={"2"}>Название</Col>
                                         <Col sm={"10"}>{object.name}</Col>
                                     </Row>
                                     <Row className={"mb-3"}>
                                         <Col sm={"2"}>Фотографии</Col>
                                         <Col sm={"10"}>
                                             {object.photos && object.photos.length > 0
                                                 ?
                                                 <div className={"d-flex gap-3"}>
                                                     {object.photos && object.photos.map((photo: any) => {
                                                         const srcUrl = import.meta.env.MODE === 'development' ? import.meta.env.VITE_SERVER_URL_DEV :
                                                             "/";
                                                         return (
                                                             <img key={photo.id} alt={""}
                                                                  height={100}
                                                                  src={`${srcUrl}${photo.path}`}/>
                                                         )
                                                     })}
                                                 </div>
                                                 :
                                                 <i className={"text-muted fs-11"}>Не загружены</i>
                                             }
                                         </Col>
                                     </Row>

                                     <Row className={"mb-3"}>
                                         <Col sm={"2"}>Комментарий</Col>
                                         <Col sm={"10"}>
                                             {object.comment.length > 0
                                                 ?
                                                 object.comment :
                                                 <i className={"text-muted fs-11"}>Не указан</i>
                                             }
                                         </Col>
                                     </Row>
                                     <Row className={"mb-3"}>
                                         <Col sm={"2"}>Создан</Col>
                                         <Col sm={"10"}>{new Date(object.createdAt).toLocaleString()}</Col>
                                     </Row>

                                     {user.role === "ADMIN" && (
                                         <Row className={"mb-3"}>
                                             <Col sm={"2"}>Кем создан</Col>
                                             <Col sm={"10"}>
                                                 {(() => {
                                                     if (object.user) {
                                                         if (object.user.role === "ADMIN") {
                                                             return (<div>Администратор ({object.user.email})</div>);
                                                         }
                                                         if (object.user.role === "USER") {
                                                             return (<div>Пользователь ({object.user.email})</div>);
                                                         }
                                                     }
                                                     return null;
                                                 })()}
                                             </Col>
                                         </Row>
                                     )}
                                 </>
                                )}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleHideModal}>Закрыть</Button>
                            </Modal.Footer>
                        </>
                        :
                        <>
                            <Modal.Body>
                                <Alert variant={"warning"}>Объект не найден</Alert>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleHideModal}>Закрыть</Button>
                            </Modal.Footer>
                        </>
                    }
                </>
            }
        </Modal>
    );
};

export default ObjectModalView;